import { g as getRoomType } from './get-room-type.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * ** SiteMinder **
 *
 * @exports bs_14
 * @category engines
 */
function factory({ window, document, thn }) {
  const engine = {};
  const isMotelDurango = thn.data.accountUserId === 1043785;
  let priceUpdate;
  let refreshPrice;

  /**
     * @memberOf module:bs_14
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {
      if (!window.location.hostname.match(/direct-book.com/)) {
        let lang = window.location.pathname.match(ctx.urlRe);
        lang = thn.parseUrlParameter('locale') || (lang && lang.length && lang[1]);

        if (lang === 'tc') {
          resolve('zh-TW');
          return;
        }
        if (lang && ctx.langs.test(lang)) {
          const language = lang === 'sc' ? 'zh' : lang;
          resolve(language);
          return;
        }
        if (thn.data.propertyId === 1024712) {
          resolve('de');
          return;
        }
        // bekehotels.com is implemented in Spanish only
        if (thn.data.accountUserId === 1040241 && window.location.hostname.match(/bekehotels/)) {
          resolve('es');
          return;
        }
        if (thn.data.accountUserId === 1044071) {
          lang = document.querySelector('.dropdown.language span')?.textContent?.toLowerCase();
          resolve(lang);
        }
      }
      reject('no_language');
    });
  };

  /**
     * @memberOf module:bs_14
     * @see {@link onStateChange}
     *
     * @param {object} ctx - The context object.
     */
  engine.onStateChange = thn.debounce(() => {
    thn.languageCheck();
    thn.pageCheck();
  }, 600);

  /**
     * @memberOf module:bs_14
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot(_ctx) {
    const spinner = document.querySelector('.el-loading-mask, .dropdown-body, .calendar-modal');

    if (spinner) {
      const siteminderPageRefresh = thn.debounce(function () {
        thn.languageCheck();
        thn.pageCheck();
      }, 800);

      thn.siminderPageUpdater = function () {
        siteminderPageRefresh();
      };

      thn.observeDom(spinner, thn.siminderPageUpdater, {
        attributes: true,
      });

      thn.shutdown(function () {
        siteminderPageRefresh.cancel();
        thn.stopObserveDom(spinner, thn.siminderPageUpdater);
        delete thn.siminderPageUpdater;
      });
    }

    const hadlerButtonUpdate = thn.delegate('click', '.occupancy-container [data-sm-test=occupancy-select-done]', () => {
      setTimeout(() => {
        thn.languageCheck();
        if (thn.data.pageName === 'Rooms and Rates') {
          thn.pageCheck({ force: true });
        }
      }, 500);
    });
    thn.shutdown(() => {
      hadlerButtonUpdate();
    });
  };

  /**
    * @memberOf module:bs_14
    * @this thn.widgets.price
    * @see {@link Price#observe_dom}
    *
    * @param {object} ctx - The context object.
    */
  engine.priceObserveDom = function priceObserveDom(ctx) {
    const container = document.querySelector('.el-main');
    if (container && !priceUpdate) {
      priceUpdate = thn.debounce(() => {
        const isNotLoading = document.querySelector('[id*=room-rate]');
        if (isNotLoading) {
          ctx.widget.refresh();
        }
      }, 500);
      thn.observeDom(container, priceUpdate, {
        childList: true,
        subTree: true,
      });

      thn.pageUnload(() => {
        priceUpdate.cancel();
        thn.stopObserveDom(container, priceUpdate);
        priceUpdate = null;
      });
    }

    // Observer to update price when selecting dates on the calendar and not available dates are shown
    const calendarDropdown = document.querySelector('.input-bar--desktop');
    if (calendarDropdown && !refreshPrice) {
      refreshPrice = thn.debounce(() => {
        const isCalendarVisible = thn.isVisible(document.querySelector('.availability-calendar.calendar'));
        const availabilityWarning = isCalendarVisible && thn.isVisible(document.querySelector('.availability-calendar .error, .calendar-modal .error, .availability-calendar div[role="alert"]'));
        if (availabilityWarning) {
          ctx.widget.refresh();
        }
      }, 200);
      thn.observeDom(calendarDropdown, refreshPrice, {
        childList: true,
        attributes: true,
        subTree: true,
      });

      thn.pageUnload(() => {
        refreshPrice.cancel();
        thn.stopObserveDom(calendarDropdown, refreshPrice);
        refreshPrice = null;
      });
    }
  };

  /**
     * @memberOf module:bs_14
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {
    const propertiesWith6YearInfant = [1157578];
    // https://direct-book.com/properties/krugergatehoteldirect?locale=en&checkInDate=2025-04-22&checkOutDate=2025-04-23&items[0][adults]=1&items[0][children]=0&items[0][infants]=0&currency=ZAR&trackPage=yes
    // https://direct-book.com/properties/krugergatehoteldirect?locale=en&checkInDate=2025-04-22&checkOutDate=2025-04-23&items[0][adults]=2&items[0][children]=0&items[0][infants]=0&items[0][rateId]=509567&items[1][adults]=2&items[1][children]=1&items[1][infants]=0&items[2][adults]=1&items[2][children]=0&items[2][infants]=0&currency=ZAR&trackPage=yes&selected=0
    // https://www.hoteltheroosevelt.com/en/choose-your-room/?check_in=10%2F09%2F2025&check_out=12%2F09%2F2025

    const url = new URL(ctx.baseUrl);
    const validUrl = url.hostname.match(/book-directonline|reserva|direct-book|booking/) || url.pathname.match(/choose-your-room/);

    if (!validUrl) {
      ctx.newUrl = '';
      return ctx;
    }

    // Get rid of all the search params
    url.search = '';

    url.searchParams.set('locale', '[language]');
    url.searchParams.set('checkInDate', '[dateBegin]');
    url.searchParams.set('checkOutDate', '[dateEnd]');
    url.searchParams.set('currency', '[currency]');

    if (ctx.data.roomsDispo?.length) {
      for (const [index, room] of Object.entries(ctx.data.roomsDispo)) {
        url.searchParams.set(`items[${index}][adults]`, room.adults);
        if (propertiesWith6YearInfant.includes(thn.data.propertyId)) {
          url.searchParams.set(`items[${index}][children]`, (room.childrenAges?.filter((age) => age >= 6)?.length || 0));
          url.searchParams.set(`items[${index}][infants]`, (room.childrenAges?.filter((age) => age < 6)?.length) || 0);
        } else {
          url.searchParams.set(`items[${index}][children]`, (room.childrenAges?.filter((age) => age >= 2)?.length || 0));
          url.searchParams.set(`items[${index}][infants]`, (room.childrenAges?.filter((age) => age < 2)?.length) || 0);
        }
      }
    }

    // Format data
    ctx.newData.language = ctx.data.language || 'en';
    ctx.newData.dateBegin = ctx.data.dateBegin?.replace(/\//g, '-');
    ctx.newData.dateEnd = ctx.data.dateEnd?.replace(/\//g, '-');

    ctx.newUrl = url.toString();

    return ctx;
  };

  /**
    *
    * @memberOf module:bs_14
    *
    */
  const roomDetailsParseData = () => [...document.querySelectorAll('.room-type[id]')].filter((el) => !el.querySelector('.room-sold-out')).map((room) => {
    const roomName = room.querySelector('span.title')?.textContent;
    const roomType = getRoomType(roomName);
    const roomRates = [...room.querySelectorAll('.room-rate-wrapper')].map((rate) => {
      const rateName = rate.querySelector('.rate-name')?.innerText;
      const ratePrice = thn.parseNumber(rate.querySelector('.price--now')?.textContent);
      // There are no selectors to indicate if the rate is refundable and with breakfast included.
      // This means that not all cases are known with certainty.
      // But if the rate is flexible, it will also be refundable and with breakfast included.
      const isFlexibleRate = rateName?.toLowerCase().includes('flex');
      const rateData = {
        id: rate.id,
        name: rateName,
        totalNight: ratePrice,
        breakfast: isFlexibleRate,
        refundable: isFlexibleRate,
      };

      return rateData;
    });

    return {
      id: room.id,
      name: roomName,
      adults: thn.parseNumber(room.querySelector('.sleep-capacity')?.nextSibling?.textContent) || 2,
      children: 0,
      type: roomType,
      prices: roomRates,
    };
  });

  /**
     * @memberOf module:bs_14
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v1 = function priceParseData_v1(ctx) {
    const widgetData = {};

    const isNotLoading = document.querySelector('[id*=room-rate], .room-sold-out');

    if (!isNotLoading) {
      widgetData.set = false;
      return widgetData;
    }

    const beObject = window.dataLayer || window.digitalPartner;

    if (!beObject) {
      ctx.widget.logger.debug('No dataLayer found, cannot retrieve price widget data');
      return widgetData;
    }

    // Dates
    function getDateFromCalendar(selector) {
      const dateEl = document.querySelector(selector);
      if (dateEl && thn.isVisible(dateEl)) {
        const dateText = dateEl.textContent?.trim();
        const dateMonthYear = dateEl?.closest('section')?.querySelector('.month-label')?.textContent;
        return translateDate(`${dateText} ${dateMonthYear}`, thn.data.language || 'en');
      }
      return null;
    }

    const isCalendarVisible = thn.isVisible(document.querySelector('.availability-calendar.calendar'));
    const noAvailability = thn.isVisible(document.querySelector('.availability-calendar .error, .calendar-modal .error, .availability-calendar div[role="alert"]'));

    const dateBegin = isCalendarVisible ? getDateFromCalendar('.day--start.day--active') : (thn.parseUrlParameter('checkInDate') || thn.objectGet(beObject, 'checkIn', { first: false }));
    const dateEnd = isCalendarVisible ? getDateFromCalendar('.day--end.day--active') : (thn.parseUrlParameter('checkOutDate') || thn.objectGet(beObject, 'checkOut', { first: false }));

    let nights = 0;

    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin.replace(/-/g, '/');
      widgetData.dateEnd = dateEnd.replace(/-/g, '/');
      nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
    }

    // Guests, Adults & Children
    const adults = thn.parseNumber(thn.dataLayerHelper.get('numberOfGuests') || thn.objectGet(beObject, 'adults', { first: false }) || 2);

    const infants = thn.parseNumber(thn.dataLayerHelper.get('numberOfInfants') || thn.objectGet(beObject, 'infants', { first: false }) || 0);

    const children = thn.parseNumber(thn.dataLayerHelper.get('numberOfChildren') || thn.objectGet(beObject, 'children', { first: false }) || 0);

    const roomInfo = {
      adults: adults,
      children: children + infants,
      childrenAges: [],
    };

    for (let i = 1; i <= roomInfo.children; i += 1) {
      // Populate children ages, with 10 for each child (2-10) and 1 for each infant (0-2)
      roomInfo.childrenAges.push(i <= widgetData.children ? 10 : 1);
    }

    widgetData.roomsDispo = [roomInfo];

    widgetData.adults = adults;

    widgetData.children = children + infants;

    widgetData.guests = widgetData.adults + widgetData.children;

    widgetData.rooms = 1;

    // Prices & Currency || No Rooms
    let price;
    // Motel Durango has both rooms and hostel. If we do not distinguish between these two cases, the lowest price of one bed in the hostel rates will always be displayed in the PW.
    if (isMotelDurango) {
      // hostel elements & rates
      const hostelEls = [...document.querySelectorAll('.room-type .bed .price--now')];
      const hostelRates = [...hostelEls].map((hostelEl) => thn.parseNumber(hostelEl?.textContent) * widgetData.guests); // Total price for number of guests searched
      const roomEls = [...document.querySelectorAll('.room-type .room .price--now')];
      // room elements & rates
      const roomRates = [...roomEls].map((roomEl) => thn.parseNumber(roomEl?.textContent));
      const prices = [...hostelRates, ...roomRates].filter(Boolean);
      price = Math.min(...prices);
    } else if (thn.objectGet(beObject, 'value', { first: false })) {
      // Preferred method, if available in the dataLayer
      price = thn.objectGet(beObject, 'value', { first: false }).bestPrice;
    } else if (document.querySelectorAll('.price').length) {
      // Fallback method, if the dataLayer does not contain the price information
      const prices = [...document.querySelectorAll('.book-price')].map((el) => {
        const notAvailableRate = el.querySelector('button')?.getAttribute('disabled') === 'disabled';
        if (!notAvailableRate) {
          return thn.parseNumber(el.querySelector('.price')?.textContent);
        }
        return false;
      }).filter(Boolean);
      price = Math.min(...prices);
    }
    if (!price) {
      const prices = [...document.querySelectorAll('[class^="price--"]')]
        .filter((priceElement) => thn.isVisible(priceElement))
        .map((priceElement) => thn.parseNumber(priceElement.textContent))
        .filter(Boolean);
      price = prices.length ? Math.min(...prices) : null;
    }
    if (!price && noAvailability) {
      return null;
    }

    const currency = thn.parseUrlParameter('customerCurrency') || thn.parseUrlParameter('currency');
    if (price && currency && nights && !noAvailability) {
      widgetData.price = price / nights;
      widgetData.currency = currency;
      widgetData.roomDetails = roomDetailsParseData();
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_14
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v2 = function priceParseData_v2(ctx) {
  // this version is for URLs with the structure "choose-your-room"
    const widgetData = {};

    // Dates
    const dateBegin = (thn.parseUrlParameter('check_in') || document.querySelector('[class*=check-in]')?.value)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
    const dateEnd = (thn.parseUrlParameter('check_out') || document.querySelector('[class*=check-out]')?.value)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
    if (!window.location.pathname.match(/choose-your-room/) || !dateBegin || !dateEnd) {
      widgetData.set = false;
      return widgetData;
    }

    // Guests, Adults & Children
    widgetData.adults = 2; // Default value
    widgetData.children = 0; // Default value

    // Prices & Currency || No Rooms
    const prices = [...document.querySelectorAll('[class*=-price]')]?.map((price) => thn.parseNumber(price.textContent)).filter(Boolean);
    const currency = thn.parseCurrency(document.querySelector('[class*=-price]')?.textContent);

    if (prices.length && currency && dateBegin && dateEnd) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = currency;
      widgetData.dateBegin = dateBegin || '';
      widgetData.dateEnd = dateEnd || '';
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_14
     * @this thn.widgets.booking
     * @see {@link Booking#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingObserveDom = function bookingObserveDom(ctx) {

    const dataLayer = window.dataLayer;

    if (dataLayer && !thn.siteminderBookingObserver) {
      // Create function to delete observer upon need
      const stopObserver = function () {
        thn.siteminderBookingObserver?.cancel();
        delete thn.siteminderBookingObserver;
      };

      thn.siteminderBookingObserver = new thn.DataLayerHelper(window.dataLayer, function (model, message) {
        if ((message.event === 'conversion' || message.bookingReference || message.event === 'transactionConfirmation' || message.event === 'purchase') && ctx.widget.setData().isSet()) {
          ctx.widget.save();
          stopObserver();
        }
      }, true);

      thn.pageUnload(stopObserver);
    }
  };

  /**
     * @memberOf module:bs_14
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(_ctx) {

    const widgetData = {};
    const dataLayer = window.dataLayer;
    const bookingId = thn.objectGet(dataLayer, 'bookingRef') || ((document.querySelector('.reference-title, [data-cy="reservation-reference-id"]') || {})?.textContent?.split('#')?.length > 1 && document.querySelector('.reference-title, [data-cy="reservation-reference-id"]')?.textContent?.split('#')?.[1]?.trim());

    // Early exit
    if (!bookingId) {
      widgetData.set = false;
      return widgetData;
    }

    let dateBegin;
    let dateEnd;
    let price;
    let currency;
    let adults;
    let children;

    if (dataLayer) {
      dateBegin = thn.objectGet(dataLayer, 'checkIn', { first: false })?.replace(/-/g, '/');
      dateEnd = thn.objectGet(dataLayer, 'checkOut', { first: false })?.replace(/-/g, '/');
      price = thn.objectGet(dataLayer, 'paymentTotal');
      currency = thn.objectGet(dataLayer, 'currency');
      adults = thn.objectGet(dataLayer, 'adults', { first: false });
      children = thn.objectGet(dataLayer, 'children', { first: false });
    }

    // Dates from DOM
    if (!dateBegin || !dateEnd) {
      const dates = document.querySelectorAll('.confirmation-details li div');
      const checkIn = document.querySelector('.list-none li:nth-child(1) .whitespace-pre-line')?.textContent;
      const checkOut = document.querySelector('.list-none li:nth-child(2) .whitespace-pre-line')?.textContent;
      dateBegin = translateDate((dates?.[0]?.textContent || checkIn), thn.data.language || 'en');
      dateEnd = translateDate((dates?.[1]?.textContent || checkOut), thn.data.language || 'en');
    }

    widgetData.dateBegin = dateBegin || '';
    widgetData.dateEnd = dateEnd || '';

    // Guests
    if (!adults || !children) {
      const guests = document.querySelectorAll('[data-cy="booking-summary-rooms"] > div > div > div:nth-child(2) span');
      adults = guests[0]?.textContent || thn.objectGet(dataLayer, 'adults', { first: false }) || 2;
      const kids = guests[1]?.textContent || thn.objectGet(dataLayer, 'children', { first: false }) || 0;
      const infants = guests[2]?.textContent || thn.objectGet(dataLayer, 'infants', { first: false }) || 0;
      children = thn.parseNumber(kids) + thn.parseNumber(infants);
    }

    widgetData.adults = thn.parseNumber(adults);
    widgetData.children = children;
    widgetData.guests = widgetData.adults + widgetData.children;

    if (!price || !currency) {
      price = document.querySelector('.total')?.textContent || document.querySelector('section > div.bg-gray-100 > div > div:nth-child(2)')?.textContent?.trim();
    }

    widgetData.price = thn.parseNumber(price);
    widgetData.currency = thn.parseCurrency(price || currency);

    // Bookig_id
    widgetData.bookingId = bookingId;

    // Take promocode if we have it
    const promocode = thn.parseUrlParameter('promocode');
    if (promocode) {
      widgetData.promocode = promocode;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_14
     * @this thn.widgets.price
     * @see {@link Price#get_promocode}
     *
     * @param {object} ctx - The context object.
     */
  engine.getPromocode = function getPromocode(ctx) {
    const dataLayer = window.dataLayer;
    if (dataLayer) {
      const promo = thn.parseUrlParameter('promocode') || thn.objectGet(dataLayer, 'promo', { first: false });
      if (promo) {
        ctx.promocode = promo;
      }
    }
    return ctx.promocode;
  };

  return engine;
}

export { factory };
